import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext"; 
import { Card, Table, Input, Button, Typography, Tag } from "antd";
import { PlusOutlined, SearchOutlined, EditOutlined } from "@ant-design/icons";
import masterService from "../../../services/masterService";
import { useCommon } from "../../../context/CommonContext";

const { Title, Text } = Typography;
const { Search } = Input;

const ContentComplaintsList = () => {
  const navigate = useNavigate();
  const { setPageTitle } = useCommon();
  const notify = useNotify();

  // State management
  const [loading, setLoading] = useState(false);
  const [contentComplaints, setContentComplaints] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) =>
      `${range[0]}-${range[1]} of ${total} content complaints`,
  });

  // Filters
  const [filters, setFilters] = useState({
    searchText: "",
  });

  const getContentComplaints = useCallback(
    async (params = {}) => {
      try {
        setLoading(true);

        const requestParams = {
          perPage: pagination.pageSize,
          currentPage: pagination.current,
          searchText: filters.searchText,
          ...params,
        };

        console.log("Fetching content complaints with params:", requestParams);
        const response = await masterService.getContentComplaints(
          requestParams
        );
        console.log("Content Complaints API Response:", response);

        if (response.data && response.data.status) {
          // Handle different possible response structures
          const contentComplaintsData =
            response.data.data?.data || response.data.data || [];
          const paginationData =
            response.data.data?.pagination || response.data.pagination || {};

          console.log("Content Complaints data:", contentComplaintsData);
          console.log("Pagination data:", paginationData);

          // Ensure contentComplaintsData is an array
          if (Array.isArray(contentComplaintsData)) {
            setContentComplaints(contentComplaintsData);
          } else {
            console.error(
              "Content Complaints data is not an array:",
              contentComplaintsData
            );
            setContentComplaints([]);
          }

          setPagination((prev) => ({
            ...prev,
            ...paginationData,
            current: paginationData.currentPage || 1,
          }));
        } else {
          notify.error({ message: "Failed to fetch content complaints" });
          setContentComplaints([]);
        }
      } catch (error) {
        console.error("Error fetching content complaints:", error);
        notify.error({ message: "Failed to fetch content complaints" });
        setContentComplaints([]);
      } finally {
        setLoading(false);
      }
    },
    [filters.searchText]
  );

  // Load content complaints data
  useEffect(() => {
    setPageTitle("Content Compliance");
    getContentComplaints();
  }, [getContentComplaints, setPageTitle]);

  // Handle search
  const handleSearch = (value) => {
    setFilters((prev) => ({ ...prev, searchText: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
    getContentComplaints({
      searchText: value,
      perPage: pagination.pageSize,
      currentPage: 1,
    });
  };

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;

    setPagination((prev) => ({
      ...prev,
      current,
      pageSize,
    }));

    getContentComplaints({
      searchText: filters.searchText,
      perPage: pageSize,
      currentPage: current,
    });
  };

  // Handle add content complaint
  const handleAddContentComplaint = () => {
    navigate("/content-compliance/add/new");
  };

  // Handle edit content complaint
  const handleEditContentComplaint = (contentId) => {
    navigate(`/content-compliance/edit/${contentId}`);
  };

  // Table columns
  const columns = [
    {
      title: "Content Name",
      dataIndex: "content_name",
      key: "content_name",
      defaultSortOrder: "ascend",
      sorter: (a, b) => a.content_name.localeCompare(b.content_name),
      render: (text) => (
        <Text strong style={{ fontSize: "14px" }}>
          {text}
        </Text>
      ),
    },
    {
      title: "Sequence",
      dataIndex: "sequence_number",
      key: "sequence_number",
      width: 100,
      render: (text) => <Text style={{ fontSize: "14px" }}>{text}</Text>,
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      width: 120,
      render: (status) => (
        <Tag color={status === "Y" ? "green" : "red"}>
          {status === "Y" ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      fixed: "right",
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditContentComplaint(record.content_id)}
          size="small"
        >
          Edit
        </Button>
      ),
    },
  ];

  // Header content with user info

  return (
      <Card>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "16px",
            gap: "16px",
            flexWrap: "wrap",
          }}
        >
          <Title level={4} style={{ margin: 0 }}>
            Content Compliance ({pagination.total})
          </Title>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              minWidth: 300,
              flex: 1,
              justifyContent: "flex-end",
            }}
          >
            <Search
              placeholder="Search content compliance..."
              allowClear
              onSearch={handleSearch}
              style={{ width: 320, maxWidth: "40vw" }}
              enterButton={<SearchOutlined />}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddContentComplaint}
              size="middle"
            >
              Add Content Compliance
            </Button>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={contentComplaints}
          rowKey="content_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
          size="middle"
          locale={{
            emptyText: "No content compliance found",
            filterConfirm: "OK",
            filterReset: "Reset",
            filterEmptyText: "No filters",
          }}
        />
      </Card>
  );
};

export default ContentComplaintsList;
